import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useHistory, Link } from 'react-router-dom';
import {
  FaEllipsisV,
  FaCog,
  FaRegCopy,
  FaBan,
  FaLevelDownAlt,
  FaUserPlus,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import Loader from '../../Loader';
import { ROUTES } from '../../../assets/constants/strings';
import {
  getAdminAccounts,
  cancelReaderPlan,
  cancelApiPlan,
  downgradeApiToTrial,
} from '../../../services/axios';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const connector = connect(mapStateToProps);

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

// Whole days from now until a date (negative once past).
const daysUntil = (d) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

// At-a-glance renewal / trial-expiry countdown for a list row. Amber within 30 days, red within 7
// (or past). Renders "—" when the account has no dated subscription.
const renewalCell = (a) => {
  if (!a.renewalDate) return <span className='adm-table__muted'>—</span>;
  const days = daysUntil(a.renewalDate);
  const when = new Date(a.renewalDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const tone =
    days < 0 ? 'adm-renew--overdue' : days <= 7 ? 'adm-renew--soon' : days <= 30 ? 'adm-renew--near' : '';
  const verb = a.renewalIsTrial ? 'Trial ends' : 'Renews';
  const rel = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'today' : `in ${days}d`;
  return (
    <span className={`adm-renew ${tone}`}>
      <span className='adm-renew__date'>{when}</span>
      <span className='adm-renew__rel'>
        {verb} · {rel}
      </span>
    </span>
  );
};

// Organisation seat usage bar (logins used of the seat cap). "—" when no cap is set.
const seatsCell = (a) => {
  if (!a.seatCap) return <span className='adm-table__muted'>—</span>;
  const used = a.seatsUsed || 0;
  const pct = Math.min(100, Math.round((used / a.seatCap) * 100));
  const full = used >= a.seatCap;
  return (
    <span className='adm-seats'>
      <span className='adm-seats__num'>
        {used} / {a.seatCap}
      </span>
      <span className='adm-seats__track'>
        <span className={`adm-seats__fill${full ? ' is-full' : ''}`} style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
};

// Customer-facing tier wording for a plan name ("API Production" → Enterprise).
const tierOf = (plan) => {
  const p = (plan || '').toLowerCase();
  if (!p) return null;
  if (p.includes('enterprise') || p.includes('production')) return 'Enterprise';
  if (p.includes('trial')) return 'Trial';
  return plan;
};

const isEnterprise = (a) => tierOf(a.readerPlan) === 'Enterprise' || tierOf(a.apiPlan) === 'Enterprise';
const isTrial = (a) => !isEnterprise(a) && (tierOf(a.readerPlan) === 'Trial' || tierOf(a.apiPlan) === 'Trial');

// One status per customer, worst-first, so the column says what needs attention.
const statusOf = (a) => {
  const days = a.renewalDate ? daysUntil(a.renewalDate) : null;
  if (days != null && days < 0)
    return { label: a.renewalIsTrial ? 'Trial lapsed' : 'Contract overdue', tone: 'adm-status--overdue' };
  if (a.renewalIsTrial && days != null && days <= 7)
    return { label: 'Trial ending', tone: 'adm-status--warn' };
  if (a.readerStatus === 'Trialing' || a.apiStatus === 'Trialing')
    return { label: 'Trial', tone: 'adm-status--trial' };
  if (a.readerPlan || a.apiPlan) return { label: 'Active', tone: 'adm-status--handled' };
  return { label: 'No subscription', tone: 'adm-status--archived' };
};

// Initials for the row avatar, from whatever names the customer.
const initials = (a) => {
  const src = a.company || a.name || a.email || '?';
  const parts = String(src).replace(/[^a-zA-Z0-9 ]/g, ' ').trim().split(/\s+/);
  if (!parts[0]) return '—';
  return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : '')).toUpperCase();
};

// Stable per-row avatar colour, so a customer keeps the same swatch between visits.
const AV_COLOURS = ['#2f6fed', '#6b4ee6', '#0f7d68', '#e08a1e', '#c0497b', '#3d7f9e'];
const avatarColour = (a) => AV_COLOURS[(a.clientId || 0) % AV_COLOURS.length];

const PRODUCT_FILTERS = [
  { key: 'all', label: 'All products' },
  { key: 'app', label: 'RegulationCity App' },
  { key: 'api', label: 'Developer API' },
  { key: 'both', label: 'Both products' },
];
const TIER_FILTERS = [
  { key: 'all', label: 'All tiers' },
  { key: 'enterprise', label: 'Enterprise' },
  { key: 'trial', label: 'Trial' },
];
const RENEWAL_FILTERS = [
  { key: 'any', label: 'Any date' },
  { key: '30', label: 'Next 30 days' },
  { key: '60', label: 'Next 60 days' },
  { key: 'overdue', label: 'Already overdue' },
];

// Admin Customers: every customer account across both products in one list, with the product, tier
// and renewal filters that used to be separate nav sections. ?product=api|app|both presets the
// product filter and ?plan=<name> narrows to one plan, so a filtered view can be linked to (the
// dashboard's API links use the first; the per-plan customer counts on Plans & limits use the second).
const Customers = ({ token, product }) => {
  const location = useLocation();
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [list, setList] = useState(null); // { items, total }
  const [listLoading, setListLoading] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const urlProduct = urlParams.get('product');
  const urlPlan = urlParams.get('plan');

  const [segment, setSegment] = useState('all');
  const [productFilter, setProductFilter] = useState(product || urlProduct || 'all');
  const [planFilter, setPlanFilter] = useState(urlPlan || '');
  const [tierFilter, setTierFilter] = useState('all');
  const [renewalFilter, setRenewalFilter] = useState('any');

  // Follow a link that arrives with a product or a plan already chosen. The plan filter has no
  // control of its own — it only ever arrives by link, and Clear filters is how it goes away.
  useEffect(() => {
    setProductFilter(product || urlProduct || 'all');
    setPlanFilter(urlPlan || '');
    setSegment('all');
  }, [product, urlProduct, urlPlan]);

  const openDetail = useCallback(
    (clientId) => history.push(`${ROUTES.APIAdmin}/customers/${clientId}`),
    [history]
  );

  // The query the loaded list was fetched with, so typing can skip a search that already ran.
  const searchedRef = useRef('');
  const searchTimer = useRef(null);

  const runSearch = useCallback(
    async (q) => {
      if (!token) return null;
      setListLoading(true);
      searchedRef.current = q || '';
      // Fetched unscoped so the segment counts and every filter describe the same set of rows.
      const res = await getAdminAccounts(token, q || '', '', 200);
      setList(res || { items: [], total: 0 });
      setListLoading(false);
      return res;
    },
    [token]
  );

  // Searching is the one control here that goes to the server, so it runs on a short pause rather
  // than per keystroke. Enter and the button below search immediately and cancel any pending run.
  useEffect(() => {
    if (search === searchedRef.current) return undefined;
    searchTimer.current = setTimeout(() => runSearch(search), 450);
    return () => clearTimeout(searchTimer.current);
  }, [search, runSearch]);

  const submitSearch = () => {
    clearTimeout(searchTimer.current);
    runSearch(search);
  };

  // --- row action menu -------------------------------------------------------
  // Fixed-positioned (like the Enquiries inbox) so it escapes the table's overflow clip.
  const [menu, setMenu] = useState(null); // { clientId, top, left }
  const [rowBusy, setRowBusy] = useState(null); // clientId being mutated
  const [rowNotice, setRowNotice] = useState(null); // { ok, text }
  const menuRef = useRef(null);
  const MENU_WIDTH = 224;
  const closeMenu = () => setMenu(null);
  const openMenu = (e, a) => {
    e.stopPropagation();
    if (menu && menu.clientId === a.clientId) {
      closeMenu();
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    setMenu({ clientId: a.clientId, top: r.bottom + 6, left: Math.max(12, r.right - MENU_WIDTH) });
  };
  useEffect(() => {
    if (!menu) return undefined;
    const onDocClick = (ev) => {
      if (menuRef.current && menuRef.current.contains(ev.target)) return;
      closeMenu();
    };
    const onDismiss = () => closeMenu();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [menu]);
  // Auto-clear the row notice after a few seconds.
  useEffect(() => {
    if (!rowNotice) return undefined;
    const t = setTimeout(() => setRowNotice(null), 5000);
    return () => clearTimeout(t);
  }, [rowNotice]);

  const copyRowEmail = (email) => {
    if (email) navigator.clipboard.writeText(email);
    closeMenu();
  };

  // Run an offboarding action against a row (confirm → call → refresh list).
  const runRowAction = async (a, fn, confirmMsg) => {
    closeMenu();
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setRowBusy(a.clientId);
    setRowNotice(null);
    const res = await fn(token, a.clientId);
    setRowBusy(null);
    if (res && res.success) {
      setRowNotice({ ok: true, text: '✓ ' + (res.reason || 'Done.') });
      runSearch(search);
    } else {
      setRowNotice({ ok: false, text: (res && res.reason) || 'Action failed.' });
    }
  };

  const rowMenuItems = (a) => {
    const items = [
      {
        key: 'manage',
        icon: <FaCog />,
        label: 'Manage customer',
        onClick: () => {
          closeMenu();
          openDetail(a.clientId);
        },
      },
    ];
    if (a.email)
      items.push({ key: 'copy', icon: <FaRegCopy />, label: 'Copy email', onClick: () => copyRowEmail(a.email) });
    const apiProduction = a.apiPlan && a.apiPlan.toLowerCase().includes('production');
    if (apiProduction)
      items.push({
        key: 'downgrade',
        icon: <FaLevelDownAlt />,
        label: 'Downgrade API to Trial',
        divider: true,
        onClick: () =>
          runRowAction(
            a,
            downgradeApiToTrial,
            'Downgrade this account to the free API Trial tier? Enterprise price, seats and call limit will be cleared.'
          ),
      });
    if (a.apiPlan)
      items.push({
        key: 'cancelapi',
        icon: <FaBan />,
        label: 'Cancel API plan',
        danger: true,
        divider: !apiProduction,
        onClick: () =>
          runRowAction(
            a,
            cancelApiPlan,
            'Cancel this API subscription and revoke the customer’s API keys? They will lose API access.'
          ),
      });
    if (a.readerPlan)
      items.push({
        key: 'cancelreader',
        icon: <FaBan />,
        label: 'Cancel App plan',
        danger: true,
        divider: !a.apiPlan,
        onClick: () =>
          runRowAction(
            a,
            cancelReaderPlan,
            'Cancel this RegulationCity App subscription? The customer will lose access.'
          ),
      });
    return items;
  };

  // Initial list + deep-link (?email= from an enquiry's "Look up").
  useEffect(() => {
    const qEmail = new URLSearchParams(location.search).get('email');
    (async () => {
      if (qEmail) {
        setSearch(qEmail);
        const res = await runSearch(qEmail);
        if (res && res.items && res.items.length === 1) openDetail(res.items[0].clientId);
      } else {
        runSearch('');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const items = (list && list.items) || [];

  const matchesProduct = (a) => {
    if (productFilter === 'app') return !!a.readerPlan;
    if (productFilter === 'api') return !!a.apiPlan;
    if (productFilter === 'both') return !!a.readerPlan && !!a.apiPlan;
    return true;
  };
  // One exact plan, on either product line — the plan a per-plan customer count was taken from.
  const matchesPlan = (a) =>
    !planFilter || a.readerPlan === planFilter || a.apiPlan === planFilter;
  const matchesTier = (a) => {
    if (tierFilter === 'enterprise') return isEnterprise(a);
    if (tierFilter === 'trial') return isTrial(a);
    return true;
  };
  const matchesRenewal = (a) => {
    if (renewalFilter === 'any') return true;
    if (!a.renewalDate) return false;
    const d = daysUntil(a.renewalDate);
    if (renewalFilter === 'overdue') return d < 0;
    return d >= 0 && d <= parseInt(renewalFilter, 10);
  };
  // Segments narrow further; the filters above always apply.
  const matchesSegment = (a) => {
    if (segment === 'enterprise') return isEnterprise(a);
    if (segment === 'trial') return isTrial(a);
    if (segment === 'expiring') {
      if (!a.renewalDate) return false;
      const d = daysUntil(a.renewalDate);
      return d >= 0 && d <= 60;
    }
    if (segment === 'overdue') return !!a.renewalDate && daysUntil(a.renewalDate) < 0;
    return true;
  };

  const filtered = items.filter(
    (a) => matchesProduct(a) && matchesPlan(a) && matchesTier(a) && matchesRenewal(a)
  );
  const visibleItems = filtered.filter(matchesSegment);

  // Counts sit on the filtered set, so they describe what the segment would actually show.
  const count = (fn) => filtered.filter(fn).length;
  const SEGMENTS = [
    { key: 'all', label: 'All customers', n: filtered.length },
    { key: 'enterprise', label: 'Enterprise', n: count(isEnterprise) },
    { key: 'trial', label: 'On trial', n: count(isTrial) },
    {
      key: 'expiring',
      label: 'Expiring in 60 days',
      n: count((a) => a.renewalDate && daysUntil(a.renewalDate) >= 0 && daysUntil(a.renewalDate) <= 60),
      // Severity rides on the count bubble, so urgency still reads without a card to tint.
      countTone: 'adm-tab__count--warn',
    },
    {
      key: 'overdue',
      label: 'Past contract end',
      n: count((a) => a.renewalDate && daysUntil(a.renewalDate) < 0),
      countTone: 'adm-tab__count--bad',
    },
  ];

  // Product counts come off the loaded rows, like every other count on this page, and ride on the
  // Products options — they used to be a separate summary strip whose total merely repeated the
  // "All customers" tab beside it.
  const productCount = (key) => {
    if (key === 'app') return items.filter((a) => !!a.readerPlan).length;
    if (key === 'api') return items.filter((a) => !!a.apiPlan).length;
    if (key === 'both') return items.filter((a) => !!a.readerPlan && !!a.apiPlan).length;
    return items.length;
  };

  const filtersOn =
    !!search ||
    productFilter !== 'all' ||
    !!planFilter ||
    tierFilter !== 'all' ||
    renewalFilter !== 'any' ||
    segment !== 'all';

  const clearFilters = () => {
    setSearch('');
    setProductFilter('all');
    setPlanFilter('');
    setTierFilter('all');
    setRenewalFilter('any');
    setSegment('all');
    // A ?product= or ?plan= link seeded a filter, so drop the param too or a refresh would bring
    // it back.
    if (urlProduct || urlPlan) history.replace(`${ROUTES.APIAdmin}/customers`);
  };

  // The list is capped, so say so rather than implying the loaded rows are everything.
  const loadedTotal = (list && list.total) || items.length;
  const truncated = loadedTotal > items.length;

  // What's currently narrowing the list, spelled out — the toolbar carries the count, so this says
  // which controls are responsible for it.
  const labelOf = (set, key) => (set.find((f) => f.key === key) || {}).label;
  const activeFilters = [
    search ? `“${search}”` : null,
    productFilter !== 'all' ? labelOf(PRODUCT_FILTERS, productFilter) : null,
    planFilter ? `${planFilter} plan` : null,
    tierFilter !== 'all' ? labelOf(TIER_FILTERS, tierFilter) : null,
    renewalFilter !== 'any' ? labelOf(RENEWAL_FILTERS, renewalFilter) : null,
    segment !== 'all' ? (SEGMENTS.find((s) => s.key === segment) || {}).label : null,
  ].filter(Boolean);

  return (
    <section className='adm-section'>
      <div className='adm-listhead'>
        <p className='adm-sub'>
          Every customer across both products. Select one to manage its subscriptions, team and terms.
        </p>
        <Link className='adm-btn adm-btn--primary' to={`${ROUTES.APIAdmin}/provision-trial`}>
          <FaUserPlus /> Provision customer
        </Link>
      </div>

      {/* Filter tabs — the console's existing filter idiom (as on the enquiry inbox), so a raised
          pill reads as the selected one of a set rather than as another row of statistics. */}
      <div className='adm-tabs adm-tabs--filters' role='group' aria-label='Filter customers'>
        {SEGMENTS.map((s) => (
          <button
            key={s.key}
            type='button'
            className={`adm-tab${segment === s.key ? ' is-active' : ''}`}
            aria-pressed={segment === s.key}
            onClick={() => setSegment(s.key)}
          >
            {s.label}
            <span className={`adm-tab__count ${s.countTone || ''}`}>{fmt(s.n)}</span>
          </button>
        ))}
      </div>

      <form
        className='adm-toolbar'
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
      >
        {/* Search is one control: the button belongs to the field, not to the selects beside it,
            which filter the loaded rows instantly. */}
        <div className='adm-searchbox'>
          <span className='adm-searchbox__ico' aria-hidden='true'>
            <FaSearch />
          </span>
          <input
            className='adm-searchbox__input'
            type='text'
            aria-label='Search customers'
            placeholder='Company, contact name or email'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {!!search && (
            <button
              className='adm-searchbox__clear'
              type='button'
              aria-label='Clear search'
              onClick={() => setSearch('')}
            >
              <FaTimes />
            </button>
          )}
          <button className='adm-searchbox__go' type='submit' disabled={listLoading}>
            {listLoading ? 'Searching…' : 'Search'}
          </button>
        </div>
        <select
          className={`adm-select${productFilter !== 'all' ? ' is-set' : ''}`}
          aria-label='Filter by product'
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
        >
          {PRODUCT_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label} ({productCount(f.key)})
            </option>
          ))}
        </select>
        <select
          className={`adm-select${tierFilter !== 'all' ? ' is-set' : ''}`}
          aria-label='Filter by tier'
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
        >
          {TIER_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          className={`adm-select${renewalFilter !== 'any' ? ' is-set' : ''}`}
          aria-label='Filter by renewal date'
          value={renewalFilter}
          onChange={(e) => setRenewalFilter(e.target.value)}
        >
          {RENEWAL_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        {filtersOn && (
          <button className='adm-clearfilters' type='button' onClick={clearFilters}>
            Clear filters
          </button>
        )}
        <span className='adm-toolbar__count'>
          Showing {fmt(visibleItems.length)} of {fmt(items.length)}
          {truncated ? ` loaded · ${fmt(loadedTotal)} total` : ''}
        </span>
      </form>

      {listLoading ? (
        <div className='adm-panel'>
          <Loader />
        </div>
      ) : !list || !items.length ? (
        <div className='adm-empty'>No customers found.</div>
      ) : (
        <>
          {activeFilters.length > 0 && (
            <div className='adm-resultcount'>Filtered by {activeFilters.join(' · ')}</div>
          )}
          {rowNotice && (
            <div
              className={`adm-alert ${rowNotice.ok ? 'adm-alert--ok' : 'adm-alert--err'}`}
              style={{ marginBottom: '.9rem' }}
            >
              {rowNotice.text}
            </div>
          )}
          {visibleItems.length === 0 ? (
            <div className='adm-empty'>No customers match these filters.</div>
          ) : (
            <div className='adm-panel adm-panel--flush'>
              <div className='adm-tablewrap'>
                <table className='adm-table'>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Primary contact</th>
                      <th>Products</th>
                      <th>Status</th>
                      <th>Renewal</th>
                      <th>Seats</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((a) => {
                      const st = statusOf(a);
                      return (
                        <tr key={a.clientId}>
                          <td>
                            <span className='adm-orgcell'>
                              <span
                                className='adm-avatar'
                                style={{ background: avatarColour(a) }}
                                aria-hidden='true'
                              >
                                {initials(a)}
                              </span>
                              <span>
                                <span className='adm-orgcell__name'>
                                  {a.company || a.name || a.email || `Client #${a.clientId}`}
                                </span>
                                {/* The address belongs to Primary contact beside this; repeating it
                                    here printed the same email twice whenever a customer had no
                                    contact name. */}
                                <span className='adm-orgcell__sub'>
                                  {a.company
                                    ? a.name || 'no contact name recorded'
                                    : a.name || a.email
                                    ? 'no company recorded'
                                    : `no contact attached · client #${a.clientId}`}
                                </span>
                              </span>
                            </span>
                          </td>
                          <td className='adm-table__nowrap'>
                            {a.email ? (
                              <>
                                <span className='adm-orgcell__name'>{a.name || '—'}</span>
                                <span className='adm-orgcell__sub'>{a.email}</span>
                              </>
                            ) : (
                              <span className='adm-table__muted'>—</span>
                            )}
                          </td>
                          <td className='adm-table__nowrap'>
                            <span className='adm-prodpills'>
                              {a.readerPlan && (
                                <span className='adm-ppill adm-ppill--app'>
                                  <span className='adm-dot adm-dot--reader' /> App ·{' '}
                                  {tierOf(a.readerPlan)}
                                </span>
                              )}
                              {a.apiPlan && (
                                <span className='adm-ppill adm-ppill--api'>
                                  <span className='adm-dot adm-dot--api' /> API · {tierOf(a.apiPlan)}
                                </span>
                              )}
                              {!a.readerPlan && !a.apiPlan && (
                                <span className='adm-table__muted'>—</span>
                              )}
                            </span>
                          </td>
                          <td className='adm-table__nowrap'>
                            <span className={`adm-status ${st.tone}`}>{st.label}</span>
                          </td>
                          <td className='adm-table__nowrap'>{renewalCell(a)}</td>
                          <td className='adm-table__nowrap'>{seatsCell(a)}</td>
                          <td className='adm-table__nowrap adm-table__actions'>
                            <div className='adm-rowactions'>
                              <button
                                className='adm-btn adm-btn--ghost adm-btn--sm'
                                type='button'
                                onClick={() => openDetail(a.clientId)}
                                disabled={rowBusy === a.clientId}
                              >
                                {rowBusy === a.clientId ? '…' : 'View'}
                              </button>
                              <button
                                className={`adm-kebab${
                                  menu && menu.clientId === a.clientId ? ' is-open' : ''
                                }`}
                                type='button'
                                aria-label='Customer actions'
                                aria-haspopup='true'
                                disabled={rowBusy === a.clientId}
                                onClick={(ev) => openMenu(ev, a)}
                              >
                                <FaEllipsisV />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {list.total > items.length && (
                <div className='adm-note' style={{ padding: '0 1.4rem 1rem' }}>
                  Showing {items.length} of {list.total}. Refine your search to narrow results.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {menu &&
        list &&
        (() => {
          const a = items.find((x) => x.clientId === menu.clientId);
          if (!a) return null;
          return (
            <div
              ref={menuRef}
              className='adm-menu'
              role='menu'
              style={{ top: menu.top, left: menu.left, width: MENU_WIDTH }}
            >
              {rowMenuItems(a).map((item) => (
                <React.Fragment key={item.key}>
                  {item.divider && <div className='adm-menu__divider' />}
                  <button
                    type='button'
                    role='menuitem'
                    className={`adm-menu__item${item.danger ? ' adm-menu__item--danger' : ''}`}
                    onClick={item.onClick}
                  >
                    <span className='adm-menu__icon'>{item.icon}</span>
                    {item.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          );
        })()}
    </section>
  );
};

export default connector(Customers);
